/**
 * useOfflineSync - Convenience hook for consuming the enhanced OfflineContext.
 *
 * Returns the full OfflineContextValue object which includes:
 *	 - isOnline: boolean				  - Whether the browser currently has network connectivity
 *	 - pendingCount: number				  - Total offline items awaiting sync across all stores
 *	 - isSyncing: boolean				  - True while a sync operation is in progress
 *	 - syncResults: SyncReport | null	  - Results from the last sync attempt
 *	 - triggerSync: () => Promise<void>	  - Manually flush the offline queue to the server
 *	 - addPendingItem: (storeName, data) => Promise<number> - Queue a new offline item
 *	 - refreshPendingCount: () => Promise<void> - Force re-read of pending count from IndexedDB
 *
 * Throws an error if called outside of an <OfflineProvider>, ensuring that
 * consumers get a clear message rather than undefined context values.
 *
 * Usage:
 *	 const {
 *		 isOnline,
 *		 pendingCount,
 *		 isSyncing,
 *		 syncResults,
 *		 triggerSync,
 *		 addPendingItem,
 *	 } = useOfflineSync();
 */
import { useContext } from 'react';
import { OfflineContext } from '../context/OfflineContext';
import type { OfflineContextValue } from '../context/OfflineContext';

export function useOfflineSync(): OfflineContextValue {
	const context = useContext(OfflineContext);

	// Guard: ensure this hook is only used inside a component tree
	// wrapped by <OfflineProvider>. Fail fast with a descriptive error.
	if (context === undefined) {
		throw new Error('useOfflineSync must be used within an OfflineProvider');
	}

	return context;
}
